import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Session timeout: 60 minutes (in milliseconds)
const SESSION_TIMEOUT = 60 * 60 * 1000; // 60 minutes
const TOKEN_REFRESH_INTERVAL = 50 * 60 * 1000; // Refresh token every 50 minutes
const WARNING_TIME = 5 * 60 * 1000; // Show warning 5 minutes before timeout

/**
 * Custom hook to manage user session and auto-logout after inactivity
 * @param {boolean} isAuthenticated - Whether user is authenticated
 * @param {function} onSessionExpired - Callback when session expires
 */
export function useSessionManager(isAuthenticated, onSessionExpired) {
  const navigate = useNavigate();
  const lastActivityRef = useRef(Date.now());
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const warningShownRef = useRef(false);

  // Update last activity time
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    warningShownRef.current = false;
  }, []);

  // Refresh token to extend session
  const refreshToken = useCallback(async () => {
    try {
      const token = localStorage.getItem('portal_token');
      if (!token) return;

      const response = await axios.post(
        `${API}/api/portal/refresh-token`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.access_token) {
        localStorage.setItem('portal_token', response.data.access_token);
        console.log('Token refreshed successfully');
      }
    } catch (error) {
      console.error('Failed to refresh token:', error);
      // If refresh fails, let the session expire naturally
    }
  }, []);

  // Show warning before session expires
  const showWarning = useCallback(() => {
    if (!warningShownRef.current) {
      warningShownRef.current = true;
      const confirmed = window.confirm(
        'Your session will expire in 5 minutes due to inactivity. Click OK to stay logged in.'
      );
      
      if (confirmed) {
        updateActivity();
        refreshToken();
      }
    }
  }, [updateActivity, refreshToken]);

  // Logout user
  const logout = useCallback(() => {
    localStorage.removeItem('portal_token');
    if (onSessionExpired) {
      onSessionExpired();
    } else {
      navigate('/portal');
    }
  }, [navigate, onSessionExpired]);

  // Check for inactivity
  const checkInactivity = useCallback(() => {
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;

    if (timeSinceLastActivity >= SESSION_TIMEOUT) {
      console.log('Session expired due to inactivity');
      logout();
    }
  }, [logout]);

  // Set up activity listeners
  useEffect(() => {
    if (!isAuthenticated) return;

    // Activity events to track
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Throttle activity updates to avoid too many updates
    let throttleTimer = null;
    const throttledUpdateActivity = () => {
      if (!throttleTimer) {
        throttleTimer = setTimeout(() => {
          updateActivity();
          throttleTimer = null;
        }, 1000); // Update at most once per second
      }
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, throttledUpdateActivity);
    });

    // Set up inactivity check interval (check every minute)
    const inactivityInterval = setInterval(checkInactivity, 60000);

    // Set up warning timeout (warn 5 minutes before expiry)
    warningTimeoutRef.current = setTimeout(showWarning, SESSION_TIMEOUT - WARNING_TIME);

    // Set up token refresh interval (refresh every 50 minutes)
    refreshIntervalRef.current = setInterval(refreshToken, TOKEN_REFRESH_INTERVAL);

    // Initial token refresh
    refreshToken();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledUpdateActivity);
      });
      clearInterval(inactivityInterval);
      clearTimeout(warningTimeoutRef.current);
      clearInterval(refreshIntervalRef.current);
      if (throttleTimer) {
        clearTimeout(throttleTimer);
      }
    };
  }, [isAuthenticated, updateActivity, checkInactivity, showWarning, refreshToken]);

  return {
    updateActivity,
    logout
  };
}

export default useSessionManager;
